const WRITE_HINT = /\b(add|create|save|delete|remove|edit|update|upload|send|generate|import|new position|invite|publish|archive|shortlist)\b/i;

export function readStoredAdmin() {
  try {
    const raw = localStorage.getItem('admin');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isReadonlyAdmin(admin = readStoredAdmin()) {
  return Boolean(admin?.readonly);
}

export function wizetokRole(admin = readStoredAdmin()) {
  return String(admin?.role || '').toLowerCase();
}

const ROLE_LABELS = { admin: 'Admin', hr_manager: 'Manager', recruiter: 'Recruiter', viewer: 'Viewer' };

export function roleLabel(role) {
  return ROLE_LABELS[String(role || '').toLowerCase()] || '';
}

/** Every Wizetok role held (highest first); falls back to the single `role`. */
export function wizetokRoles(admin = readStoredAdmin()) {
  const list = Array.isArray(admin?.roles) && admin.roles.length ? admin.roles : [admin?.role];
  return list.filter(Boolean).map((r) => String(r).toLowerCase());
}

export function wizetokRoleLabel(admin = readStoredAdmin()) {
  if (isReadonlyAdmin(admin)) return 'Viewer';
  return wizetokRoles(admin).map(roleLabel).filter(Boolean).join(' + ');
}

const POSITION_PAGES = ['campaigns', 'question-bank', 'candidates', 'interviews', 'monitor', 'reports'];

export function canAccessWizetokNav(itemId, admin = readStoredAdmin(), rules = null) {
  // Once Rules & Permissions are loaded they decide; before that, fall back to the role checks.
  if (rules?.loaded && !rules.failed) {
    // The Admin role on its own manages users, roles and rules and sees the numbers —
    // position pages are only for Managers, Recruiters and Viewers.
    const worksOnPositions = ['MANAGER', 'RECRUITER', 'VIEWER'].some((r) => rules.hasRole(r));
    if (POSITION_PAGES.includes(itemId) && !worksOnPositions) return false;
    if (itemId === 'administration') {
      return rules.can('users.view') || rules.hasRole('ADMIN') || rules.hasRole('MANAGER') || rules.can('audit.view');
    }
    if (itemId === 'settings') return rules.can('settings.manage');
  }
  if (itemId === 'administration') {
    // Admins, managers and recruiters manage users (each within their own rules).
    const role = wizetokRole(admin);
    return !isReadonlyAdmin(admin) && ['admin', 'hr_manager', 'recruiter'].includes(role);
  }
  if (itemId === 'settings') {
    const role = wizetokRole(admin);
    return !isReadonlyAdmin(admin) && (role === 'admin' || role === 'hr_manager');
  }
  return true;
}

export function isWizetokWritePath(pathname) {
  const path = String(pathname || '');
  if (path === '/admin/campaigns/new') return true;
  if (path.includes('/edit') && path.startsWith('/admin/campaigns/')) return true;
  return false;
}

export function installWizetokViewerWriteGuard() {
  const onClick = (event) => {
    const el = event.target.closest('button, a[href], [role="button"], input[type="submit"], input[type="file"]');
    if (!el) return;
    if (el.closest('.al-sidebar, [data-role-nav]')) return;
    if (el.type === 'file') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const label = `${el.getAttribute('aria-label') || ''} ${el.textContent || ''} ${el.title || ''}`;
    if (WRITE_HINT.test(label) || el.closest('[data-write-action]')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}
